import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiDeviation } from '@wix/da-papi-types';
import ShopThumb, {
  ShopThumbType,
} from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Shop';
import StandardThumb from '@wix/da-shared-react/pkg/DeviationViews/Thumb/Standard';
import {
  getDeviationResourceType,
  getSuggestionReasonsByPriority,
} from '@wix/da-shared-react/pkg/DeviationViews/_helpers';
import { BiEventType } from '@wix/da-bi/pkg/constants';
import { BiLoggerContextProvider } from '@wix/da-bi/pkg/BiLogger.context';
import DeviationMediaService from '@wix/da-shared-react/pkg/utils/DeviationMediaService';
import useBiDataForDeviationLink from '@wix/da-shared-react/pkg/Duperbrowse/DeviationLink/useBiDataForDeviationLink';
import {
  DeviationTypes,
  DeviationSuggestionReasons,
} from '@wix/da-shared-react/pkg/types/deviation';
import { NullableDropdownItem } from '@wix/da-shared-react/pkg/Dropdown/types';
import { Props as ItemCardProps } from './ItemCard';
import s from './ItemCard.scss';

export interface Props
  extends Pick<
    ItemCardProps,
    | 'width'
    | 'height'
    | 'style'
    | 'canShowLess'
    | 'responsive'
    | 'onShowLess'
    | 'showTierPromotionTooltip'
    | 'onRemove'
  > {
  deviation: PapiDeviation;
  offset: number;
  className?: string;
}
const nonDeviationTypes = [
  DeviationTypes.COMMISSION,
  DeviationTypes.POLL,
  DeviationTypes.STATUS,
  DeviationTypes.UNKNOWN,
];

const suggestionReasonToNameMap = {
  [DeviationSuggestionReasons.DailyDeviation]: 'daily_deviation',
  [DeviationSuggestionReasons.NewDeviant]: 'new_deviant',
  [DeviationSuggestionReasons.Popular]: 'popular',
  [DeviationSuggestionReasons.Recommended]: 'recommended',
  [DeviationSuggestionReasons.WatchingGroup]: 'watching_group',
  [DeviationSuggestionReasons.WatchingTopic]: 'watching_topic',
  [DeviationSuggestionReasons.WatchingUser]: 'watching_user',
};

const DeviationCard: React.FC<Props> = ({
  deviation,
  offset,
  width,
  height,
  style,
  responsive,
  canShowLess,
  showTierPromotionTooltip,
  className,
  onRemove,
  onShowLess,
}) => {
  const { t } = useTranslation();
  const linkBiData = useBiDataForDeviationLink(deviation);

  const reasonShown =
    getSuggestionReasonsByPriority(deviation)
      ?.map(reason => suggestionReasonToNameMap[reason] ?? 'unknown')
      .join(',') || 'unknown';
  const devType = DeviationMediaService.getDeviationType(deviation);
  const hasShowLess = canShowLess && !nonDeviationTypes.includes(devType);
  const actions: NullableDropdownItem[] = useMemo(
    () => [
      {
        label: t('itemCard.showLess'),
        onClick: () =>
          onShowLess({
            deviationId: deviation.deviationId,
            offset,
            typeId: getDeviationResourceType(deviation),
            reason: reasonShown,
            ownerId: deviation.author?.userId,
          }),
        biData: {
          ...linkBiData,
          evid: BiEventType.SHOW_LESS_LIKE_THIS,
        },
      },
    ],
    [t, linkBiData, onShowLess, deviation, offset, reasonShown]
  );
  const onRemoveItem = useMemo(
    () => onRemove && (() => onRemove(deviation, offset)),
    [deviation, onRemove, offset]
  );

  const isDas = deviation.hasOwnProperty('digitalAsset');
  const thumb =
    devType === DeviationTypes.COMMISSION || isDas ? (
      <ShopThumb
        deviation={deviation}
        width={width}
        height={height}
        type={isDas ? ShopThumbType.DAS : ShopThumbType.COMMISSION}
        style={style}
        className={className}
      />
    ) : (
      <div className={classnames(s['thumb'], className)}>
        <StandardThumb
          deviation={deviation}
          width={width}
          height={height}
          style={style}
          responsive={responsive}
          extraActions={hasShowLess ? actions : undefined}
          showTierPromotionTooltip={showTierPromotionTooltip}
          onRemove={onRemoveItem}
        />
      </div>
    );
  return (
    <BiLoggerContextProvider value={{ reason_shown: reasonShown }}>
      {thumb}
    </BiLoggerContextProvider>
  );
};
DeviationCard.displayName = 'DeviationCard';

export default DeviationCard;
